/**
 * coryDiagnostics — capture lightweight device/browser context + compress
 * screenshots client-side so support gets reproducible bug reports without
 * bloating Mongo. Screenshots are stored as base64 data URLs (app convention).
 */

// Small, non-PII diagnostics blob attached to bug reports.
export function captureDiagnostics() {
  if (typeof window === 'undefined') return {};
  try {
    return {
      url: window.location?.href?.slice(0, 300) || '',
      route: window.location?.pathname || '',
      userAgent: navigator?.userAgent?.slice(0, 300) || '',
      platform: navigator?.platform || '',
      language: navigator?.language || '',
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      screen: `${window.screen?.width}x${window.screen?.height}`,
      pixelRatio: window.devicePixelRatio || 1,
      online: navigator?.onLine ?? true,
      appVersion: process.env.REACT_APP_VERSION || 'preview',
      capturedAt: new Date().toISOString(),
    };
  } catch {
    return {};
  }
}

const MAX_DIMENSION = 1200; // px — downscale large screenshots
const JPEG_QUALITY = 0.6;
export const MAX_SCREENSHOTS = 5;

/**
 * Read an image File and return a compressed JPEG/PNG data URL.
 * Downscales to MAX_DIMENSION on the longest edge to keep payloads small.
 */
export function fileToCompressedDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file || !file.type?.startsWith('image/')) {
      reject(new Error('Only image files are supported'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Could not load image'));
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > MAX_DIMENSION) {
          height = Math.round((height * MAX_DIMENSION) / width);
          width = MAX_DIMENSION;
        } else if (height > MAX_DIMENSION) {
          width = Math.round((width * MAX_DIMENSION) / height);
          height = MAX_DIMENSION;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        try {
          resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
        } catch {
          resolve(reader.result); // fall back to the raw data URL
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
